import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { DayTheme, DAY_THEME_LABELS, DAY_THEME_ICONS } from '@/types';
import styles from '@/pages/WeeklyPlanningPage.module.css';

export interface DayThemeSelectorProps {
  date: Date;
  theme: DayTheme;
  onChange: (date: string, theme: DayTheme) => void;
}

const AVAILABLE_THEMES: NonNullable<DayTheme>[] = [
  'deep-work',
  'meetings',
  'admin',
  'creative',
  'learning',
  'rest',
  'flex',
];

export function DayThemeSelector({ date, theme, onChange }: DayThemeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dateKey = format(date, 'yyyy-MM-dd');
  const dayName = format(date, 'EEE');
  const dayNum = format(date, 'd');
  const isToday = format(new Date(), 'yyyy-MM-dd') === dateKey;

  const handleSelect = (newTheme: DayTheme) => {
    onChange(dateKey, newTheme);
    setIsOpen(false);
  };

  return (
    <div className={`${styles.dayCard} ${isToday ? styles.dayCardToday : ''}`}>
      <div className={styles.dayHeader}>
        <span className={styles.dayName}>{dayName}</span>
        <span className={styles.dayNum}>{dayNum}</span>
      </div>

      <div className={styles.themeSelector}>
        <motion.button
          className={`${styles.themeTrigger} ${theme ? styles.themeActive : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          {theme ? (
            <>
              <span className={styles.themeIcon} aria-hidden="true">
                {DAY_THEME_ICONS[theme]}
              </span>
              <span className={styles.themeLabel}>{DAY_THEME_LABELS[theme]}</span>
            </>
          ) : (
            <span className={styles.themePlaceholder}>Set theme</span>
          )}
        </motion.button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              className={styles.themeDropdown}
              initial={{ opacity: 0, y: -5, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -5, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              role="listbox"
            >
              <button
                className={`${styles.themeOption} ${!theme ? styles.themeOptionActive : ''}`}
                onClick={() => handleSelect(null)}
                role="option"
                aria-selected={!theme}
              >
                <span className={styles.themeOptionIcon} aria-hidden="true">
                  -
                </span>
                <span>No theme</span>
              </button>
              {AVAILABLE_THEMES.map((t) => (
                <button
                  key={t}
                  className={`${styles.themeOption} ${theme === t ? styles.themeOptionActive : ''}`}
                  onClick={() => handleSelect(t)}
                  role="option"
                  aria-selected={theme === t}
                >
                  <span className={styles.themeOptionIcon} aria-hidden="true">
                    {DAY_THEME_ICONS[t]}
                  </span>
                  <span>{DAY_THEME_LABELS[t]}</span>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
